import React, { useContext, useEffect, Fragment } from "react";
import { useHistory } from "react-router-dom";
import classes from "./Signin.module.scss";
import { AuthContext } from "../../context/Auth/AuthContext";

import CodeValidation from "../../schemas/Validation/Login";
import { Formik } from "formik";

import TriviaInput from "../../components/inputfield/triviainput/triviainput";

import InputIcon from "../../assets/icons/questionmark.svg";
import { Header } from "../../components/Trivia/Header/Container";

import Button from "../../components/Trivia/Button/Button";
import MessageSlot from "../../components/Trivia/MessageSlot/MessageSlot";
import { BoldText, HarlowText } from "../../components/Text/Text";
import NetworkManager from "../../services/NetworkManager";
import { MAIN_MENU_ROUTE } from "../routes/RoutesConstants";
import { CircularSpinner } from "../../components/Spinner/Spinner";
import { unSubscribePusher } from "../../services/pusher";
import { GameStateContext } from "../../context/GameState/GameStateContext";
import {
  GAME_CODE,
  GAME_CODE_DATA,
  GAME_COMPANY,
  USER_NAME,
} from "../../constants";
import LocalStorage from "../../services/LocalStorage";
import { RED } from "../../color";
import { FooterText } from "../GameMenu/Menu";

export interface ILogin {
  value?: string;
  color?: string;
}
export interface ILoginForm {
  code: string;
  playerName: string;
}

const Login = () => {
  const authContext = useContext(AuthContext);
  const { setGameState } = useContext(GameStateContext);
  const history = useHistory();
  const [serverStatus, setServerStatus] = React.useState<ILogin>({
    value: "",
    color: "",
  });
  const [isLoading, setIsLoading] = React.useState<boolean>(false);

  useEffect(() => {
    setServerStatus(authContext.errorMessage);
  }, [authContext.errorMessage]);

  useEffect(() => {
    if (authContext.checkAuthentication()) {
      history.push("/");
      return;
    }
    console.log("Unsubscribe channels");
    unSubscribePusher();
  }, []); // eslint-disable-line react-hooks/exhaustive-deps

  const onTestConnectionButtonClick = async () => {
    try {
      setIsLoading(true);
      await NetworkManager.HealthCheck();
      setServerStatus({ value: "CONNECTION SUCCESSFUL!", color: "#29f937" });
    } catch (err) {
      setServerStatus({ value: "CONNECTION FAILED!", color: "#FC5C65" });
    }
    setIsLoading(false);
  };
  const onKeyDownEvent = (
    e: React.KeyboardEvent<HTMLInputElement>,
    { code, playerName }: ILoginForm
  ) => {
    if (
      e.key === "Enter" &&
      code &&
      code.length >= 8 &&
      playerName &&
      playerName.length >= 3
    ) {
      e.preventDefault();
      handleClick({ code, playerName });
    } else if (e.key === "Enter") {
      e.preventDefault();
    }
  };

  const handleClick = async ({ code, playerName }: ILoginForm) => {
    setIsLoading(true);
    try {
      //await MemberJoinValidation(code);
      const response = await NetworkManager.Login(
        code.toUpperCase(),
        playerName.toUpperCase()
      );

      const { token, company } = response.body;
      authContext.authenticateUser(token);

      LocalStorage.SetItem(GAME_CODE, code.toUpperCase());
      LocalStorage.SetItem(GAME_CODE_DATA, JSON.stringify(response.body));
      LocalStorage.SetItem(USER_NAME, playerName.toUpperCase());
      LocalStorage.SetItem(GAME_COMPANY, company);
      if (setGameState)
        setGameState({
          gameCode: code.toUpperCase(),
        });
      history.push(MAIN_MENU_ROUTE);
    } catch (error) {
      const err = error as any;
      setServerStatus({
        value: err.response.data.message,
        color: RED,
      });
      setIsLoading(false);
    }
  };

  const loginPage = () => {
    return (
      <header className={classes["menu-header"]}>
        <Header
          className={classes["loginHeaderResponsive"]}
          fontSize="8.93em"
          text={["VIRTUAL TRIVIA!"]}
          height="224px"
          classHeader={classes["header"]}
        >
          <HarlowText
            size={"2.93em"}
            className={classes["welcomeText"]}
            style={{
              transform: `rotate(${-1.2}deg)`,
            }}
          >
            welcome to...
          </HarlowText>
          <div className={classes["loginContainer"]}>
            <Formik
              initialValues={{
                code: "",
                playerName: "",
              }}
              validationSchema={CodeValidation}
              onSubmit={handleClick}
            >
              {({ values, handleChange, handleSubmit, isSubmitting }) => {
                return (
                  <>
                    <div className={classes["loginContent"]}>
                      <BoldText
                        size={"2.18em"}
                        style={{ color: "#fff" }}
                        className={classes["loginDetails"]}
                      >
                        ENTER YOUR GAME CODE AND NAME TO GET QUIZZIN'
                      </BoldText>

                      <div className={classes["gameCode"]}>
                        <form autoComplete="off">
                          <TriviaInput
                            autoFocus
                            value={values.code}
                            className={classes["gameCodeInput"]}
                            onValueChanged={handleChange}
                            id="code"
                            name="code"
                            placeholder={"ENTER YOUR GAME CODE"}
                            maxLength={8}
                            icon={InputIcon}
                            tooltipClass={classes["tooltipIcon"]}
                            onKeyDown={(e) => onKeyDownEvent(e, values)}
                          >
                            Your game code is 8 characters long and can be{" "}
                            <br />
                            found in the confirmation email. All players
                            <br /> use the same game code to log in.
                          </TriviaInput>
                        </form>
                      </div>
                      <div className={classes["gameCode"]}>
                        <form autoComplete="off">
                          <TriviaInput
                            className={classes["gameCodeInput"]}
                            value={values.playerName}
                            onValueChanged={handleChange}
                            id="playerName"
                            name="playerName"
                            placeholder={"ENTER YOUR NAME"}
                            maxLength={10}
                            icon={InputIcon}
                            tooltipClass={classes["tooltipIcon"]}
                            onKeyDown={(e) => onKeyDownEvent(e, values)}
                          >
                            Enter your unique 3-10 character name to show within
                            the game.
                          </TriviaInput>
                        </form>
                      </div>
                    </div>
                    <div className={classes["buttonContainer"]}>
                      <Button
                        leftArrow={true}
                        color={"#D4ABFC"}
                        style={{ fontSize: "2.75em" }}
                        width={373}
                        height={91}
                        type="button"
                        className={classes["connectionButton"]}
                        onClick={() => {
                          onTestConnectionButtonClick();
                        }}
                      >
                        TEST CONNECTION
                      </Button>
                      <form onSubmit={handleSubmit} autoComplete="off">
                        <Button
                          leftArrow={false}
                          color={
                            values.code.length < 8 ||
                            values.playerName.length < 3
                              ? "#B5B3C1"
                              : "#F9CE29"
                          }
                          style={{ fontSize: "2.75em" }}
                          width={314}
                          height={91}
                          type={"submit"}
                          disabled={
                            isSubmitting ||
                            isLoading ||
                            values.code.length < 8 ||
                            values.playerName.length < 3
                          }
                          className={classes["connectButton"]}
                        >
                          CONNECT
                        </Button>
                      </form>
                    </div>
                  </>
                );
              }}
            </Formik>
          </div>
          {!serverStatus.value ? (
            <span></span>
          ) : (
            <div className={classes["error"]}>
              <MessageSlot
                color={serverStatus.color || "#29f937"}
                style={{ fontSize: "40px", transform: "rotate(-1deg)" }}
              >
                {serverStatus.value}
              </MessageSlot>
            </div>
          )}
        </Header>
        {FooterText()}
      </header>
    );
  };

  return (
    <Fragment>
      {isLoading ? <CircularSpinner></CircularSpinner> : null}
      {loginPage()}
    </Fragment>
  );
};

export default Login;
